import { forwardRef, useMemo, useEffect } from "react";
import styled from "styled-components";
import ReactEcharts from "echarts-for-react";
import * as echarts from "echarts/core";
import type { ECharts, EChartsOption } from "echarts";
import type {
  BaseChartConfigTypes,
  EChartChartReadyHandler,
  EChartClickHandler,
  EChartDataZoomHandler,
  EChartFinishedHandler,
  EChartMouseOutHandler,
  EChartMouseOverHandler,
  EChartRenderedHandler,
  EChartRestoreHandler,
  ReactEchartsRef,
} from "../../types/echarts";
import { useSelector } from "react-redux";
import { RootState } from "../../state/store";
import { configureEcharts, chartIcons } from "./utils";

interface IBaseChartProps {
  forwardedRef?: React.Ref<any>;
  autoHeightResize?: boolean;
  notMerge?: boolean;
  lazyUpdate?: boolean;
  echartsTheme?: any;
  onChartReady?: EChartChartReadyHandler;
  onClick?: EChartClickHandler;
  onMouseOver?: EChartMouseOverHandler;
  onMouseOut?: EChartMouseOutHandler;
  onDataZoom?: EChartDataZoomHandler;
  onRestore?: EChartRestoreHandler;
  onFinished?: EChartFinishedHandler;
  onRendered?: EChartRenderedHandler;
  onLegendSelectChanged?: any;
  style?: any;
  height: string | number;
  width: string | number;
  options: EChartsOption;
  chartType: BaseChartConfigTypes;
  renderer?: "svg" | "canvas" | undefined;
  showScrollbar?: boolean;
}

const BaseChartUnwrapped = ({
  autoHeightResize = false,
  notMerge = false,
  lazyUpdate = false,
  options = {},
  height = 200,
  width,
  forwardedRef,
  onClick,
  onMouseOver,
  onMouseOut,
  onDataZoom,
  onRestore,
  onFinished,
  onRendered,
  style = {},
  onChartReady,
  chartType,
  renderer = "svg",
  showScrollbar = false,
}: IBaseChartProps) => {
  const { theme } = useSelector((state: RootState) => state);
  
  // Register necessary ECharts components based on chart type
  useEffect(() => {
    const components = configureEcharts(chartType);
    components.forEach(component => {
      echarts.use(component);
    });
  }, [chartType]);

  const eventsMap = useMemo(
    () => ({
      click: (props: any, instance: any) => {
        handleClick(props, instance);
        onClick?.(props, instance);
      },
      mouseover: (props: any, instance: any) => {
        onMouseOver?.(props, instance);
      },
      mouseout: (props: any, instance: any) => {
        onMouseOut?.(props, instance);
      },
      datazoom: (props: any, instance: any) => {
        onDataZoom?.(props, instance);
      },
      restore: (props: any, instance: any) => {
        onRestore?.(props, instance);
      },
      finished: (props: any, instance: any) => {
        onFinished?.(props, instance);
      },
      rendered: (props: any, instance: any) => {
        onRendered?.(props, instance);
      },
    }),
    [onClick, onMouseOver, onMouseOut, onDataZoom, onRestore, onFinished, onRendered]
  );

  const handleClick = (clickSeries: any, instance: ECharts) => {
    if (clickSeries.data) {
      clickSeries.data.onClick?.(clickSeries, instance);
    }
  };

  const chartOption: EChartsOption = useMemo(() => {
    let overrideConfigs: EChartsOption = {
      legend: {
        icon: "circle",
      },
      textStyle: {
        fontFamily:
          "ibm-plex-sans, Helvetica Neue, Helvetica, Arial, sans-serif",
      },
      color: [
        "#2DB6F5",
        "#39C86A",
        "#F5C324",
        "#FF8A65",
        "#E289F2",
        "#FF6B6B",
        "#54A0FF",
        "#0D8A6A",
        "#FF9F43",
        "#1C5085",
        "#58585E",
        "#607D8B",
        "#BE4242",
        "#995F28",
      ],
    };
    
    // Merge legend properties
    overrideConfigs.legend = {
      ...(options.legend || {}),
      icon: "circle",
    };
    
    return { ...options, ...overrideConfigs };
  }, [options, showScrollbar]);

  return (
    <ChartContainer>
      <ReactEcharts
        ref={forwardedRef}
        notMerge={notMerge}
        lazyUpdate={lazyUpdate}
        theme={theme.mode}
        onChartReady={onChartReady}
        onEvents={eventsMap}
        style={{
          height,
          width,
          ...style
        }}
        opts={{
          renderer,
        }}
        option={chartOption}
      />
    </ChartContainer>
  );
};

const BaseChart = forwardRef<ReactEchartsRef, IBaseChartProps>((props, ref) => (
  <BaseChartUnwrapped forwardedRef={ref} {...props} />
));

BaseChart.displayName = "BaseChart";

export default BaseChart;

const ChartContainer = styled.div`
  display: inline-block;
  width: 100%;
  height: 100%;
`;
